/**
 * Shared helpers for the Adult Autism review data shape (used by both the SSI
 * Review and the Behavioural Observation Review).
 */

import { ASD_CONDITION_LABEL } from '../constants/autismConditions';

/**
 * Migrates a saved review from the legacy shape (where Autism Spectrum Disorder
 * was just another pinned entry in the differentials picker) into the new shape
 * where ASD is the dedicated "Condition(s) Referred For" entry.
 *
 * The transformation is idempotent and safe to run on every read:
 *   • If explicit `referredConditionNotes` already exist they win — we never
 *     overwrite a clinician's referred-condition notes.
 *   • Otherwise, if ASD is present in the legacy differentials, its per-condition
 *     notes are promoted to become the referred-condition notes.
 *   • Either way, ASD is stripped out of the differentials list, its
 *     per-condition notes map entry, and any custom-conditions list, so the same
 *     condition can never appear in two places.
 *
 * @param {object} savedReview - the raw review object read from Firestore
 * @returns {{ referredConditionNotes: Array, formulationConditions: string[],
 *   formulationConditionNotes: object, customConditions: string[] }}
 */
export function splitLegacyAsdDifferential(savedReview) {
  const conditions = Array.isArray(savedReview?.formulationConditions)
    ? savedReview.formulationConditions
    : [];
  const notes = (savedReview?.formulationConditionNotes && typeof savedReview.formulationConditionNotes === 'object')
    ? savedReview.formulationConditionNotes
    : {};
  const customConditions = Array.isArray(savedReview?.customConditions)
    ? savedReview.customConditions
    : [];

  const hasAsdInDifferentials = conditions.includes(ASD_CONDITION_LABEL);

  // Explicit referred notes (new data) take precedence over legacy migration.
  let referredConditionNotes = Array.isArray(savedReview?.referredConditionNotes)
    ? savedReview.referredConditionNotes
    : null;
  if (referredConditionNotes == null) {
    referredConditionNotes = hasAsdInDifferentials
      ? (Array.isArray(notes[ASD_CONDITION_LABEL]) ? notes[ASD_CONDITION_LABEL] : [])
      : [];
  }

  const cleanedNotes = { ...notes };
  delete cleanedNotes[ASD_CONDITION_LABEL];

  return {
    referredConditionNotes,
    formulationConditions: conditions.filter(c => c !== ASD_CONDITION_LABEL),
    formulationConditionNotes: cleanedNotes,
    customConditions: customConditions.filter(c => c !== ASD_CONDITION_LABEL),
  };
}

/**
 * Normalises a raw notes value (from Firestore or legacy localStorage) into the
 * StructuredNotesEditor array shape. Accepts an existing array, a non-empty
 * string (wrapped into a single note), or returns an empty array otherwise.
 *
 * @param {Array|string|undefined|null} raw
 * @returns {Array}
 */
export function normaliseNotesValue(raw) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    return [{ id: Date.now(), text: raw.trim(), type: 'note' }];
  }
  return [];
}
